import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LoneWorkContactsPut } from '@cloudsign/shared';
import { api } from '../../../lib/api';
import { t } from '../../../lib/i18n';
import { useCan } from '../../../lib/session';
import { Button, Card, Spinner } from '../../../components/ui';
import { ErrorLine, ErrorPanel } from '../../../components/ErrorPanel';
import { useToast } from '../../../components/Toast';
import { ContactList, contactsKey } from './api';
import { ContactRows, contactProblem, contactsBody } from './ContactRows';
import type { ContactDraft } from './ContactRows';

/**
 * Member page tab "Lone working contacts" (lone-working.md s6). Exported for W23 to mount in the member drawer.
 * Shows nothing without `lone_working.manage` (the server checks every call too).
 */
export function LoneWorkContactsTab({ memberId }: { memberId: string }) {
  const canManage = useCan('lone_working.manage');
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: contactsKey(memberId), enabled: canManage, queryFn: () => api.get(`/lone-working/members/${memberId}/contacts`, { schema: ContactList }) });
  const [rows, setRows] = useState<ContactDraft[] | null>(null);
  useEffect(() => {
    if (q.data) setRows([...q.data].sort((a, b) => a.order - b.order).map((c) => ({ name: c.name, phone: c.phone ?? '', email: c.email ?? '' })));
  }, [q.data]);
  const save = useMutation({
    mutationFn: () => api.put(`/lone-working/members/${memberId}/contacts`, { body: LoneWorkContactsPut.parse({ contacts: contactsBody(rows ?? []) }), schema: ContactList }),
    onSuccess: (r) => {
      qc.setQueryData(contactsKey(memberId), r);
      toast(t('p3lw.contacts.saved'));
    },
  });
  if (!canManage) return null;
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  if (!rows) return <Spinner />;
  const valid = rows.every((r) => contactProblem(r) === null);
  return (
    <Card
      title={t('p3lw.contacts.title')}
      description={t('p3lw.contacts.description')}
      footer={
        <Button variant="primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>
          {t('common.save')}
        </Button>
      }
    >
      <div className="space-y-4">
        <ContactRows rows={rows} onChange={setRows} label={t('p3lw.contacts.legend')} />
        <ErrorLine error={save.error} />
      </div>
    </Card>
  );
}
